import React, { useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { developerInfo } from '../data/mock';
import hero from "../assets/images/hero.png"

const HeroSection = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 50;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(139, 92, 246, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      
      particles.forEach((a, i) => {
        particles.slice(i + 1).forEach((b) => {
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.strokeStyle = `rgba(139, 92, 246, ${0.15 * (1 - distance / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black">
      
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />

     
      <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          
          <div className="text-left space-y-6 animate-in fade-in slide-in-from-left duration-700">
            <div className="inline-block">
              <span className="text-blue-400 text-sm font-mono bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20">
                Desenvolvedor Full Stack
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              {developerInfo.name}
            </h1>

            <div className="flex flex-wrap gap-2 text-lg md:text-xl text-gray-400 font-mono">
              {developerInfo.stacks.map((stack, index) => (
                <React.Fragment key={stack}>
                  <span className="text-purple-400 hover:text-purple-300 transition-colors cursor-default">
                    {stack}
                  </span>
                  {index < developerInfo.stacks.length - 1 && (
                    <span className="text-gray-600">•</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            <p className="text-gray-400 text-lg max-w-xl leading-relaxed">
              {developerInfo.description}
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                onClick={() => scrollToSection('#projects')}
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg shadow-purple-500/20"
              >
                Ver Projetos
              </Button>
              <Button
                onClick={() => scrollToSection('#contact')}
                size="lg"
                variant="outline"
                className="border-gray-700 text-white hover:bg-white/5"
              >
                Entrar em Contato
              </Button>
            </div>
          </div>

          
          <div className="relative animate-in fade-in slide-in-from-right duration-700 delay-300">
            <div className="relative w-full max-w-md mx-auto">
              
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-2xl blur-3xl" />
              
              
              <div className="relative bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-2xl p-1 border border-purple-500/20">
                <div className="bg-black/40 backdrop-blur-sm rounded-xl flex items-center justify-center "> 
                 
                  <div className="text-center space-y-4">
                    
                    <img src={hero} alt="Foto de Daniel Lima gerada por IA" className='rounded-2xl'/>
                  </div>
                </div>
              </div>

              
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/10 rounded-full blur-xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-600/10 rounded-full blur-xl" />
            </div>
          </div>
        </div>
      </div>

      
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="text-gray-500" size={32} />
      </div>
    </section>
  );
};

export default HeroSection;